import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, defer, from, map, of, throwError, catchError, EMPTY, shareReplay } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  SolicitudVacaciones,
  CreateSolicitudRequest,
  UpdateSolicitudRequest,
  EditarSolicitudAdminRequest,
  AprobarRechazarRequest,
  SaldoVacaciones,
  VacacionesDashboard,
  VacacionesCalendario,
  DirectorAsignacion,
  EmpleadoEquipoSaldo,
} from '@schemas/vacaciones.schema';
import { ApiResponse } from '@schemas/empleado.schema';
import { SolicitudVacacionesResponse } from '@schemas/rh-vacaciones';
import { RhVacacionesService } from './rh-vacaciones-service';

/**
 * Vacaciones: servicio de dominio.
 *
 * El transporte lo hace `RhVacacionesService`, generado desde el contrato. Aquí
 * se queda lo que no está en ningún contrato: la caché de directores con su
 * invalidación, la degradación a lista vacía de los listados, y los valores por
 * defecto del rango de calendario.
 *
 * La API pública sigue devolviendo `Observable`, así que los consumidores no se
 * tocaron. El puente es `defer(() => from(...))`; el `defer` mantiene la pereza
 * —sin él la petición saldría al construir el observable y la caché de abajo se
 * volvería un precargador—.
 */
@Injectable({ providedIn: 'root' })
export class VacacionesService {
  private readonly api = inject(RhVacacionesService);

  /**
   * Solo para `getSaldo`. Ver el comentario de ese método: es la única llamada
   * que necesita el código de estado HTTP, y el servicio generado lo pierde.
   */
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.BaseUrl}/Vacaciones`;

  /**
   * Caché por sesión de getDirectores(). La jerarquía rara vez cambia, pero
   * SÍ lo hace cuando RH usa el modal "Cambiar Área" (porque movemos al
   * empleado a un nuevo SuperiorId en movempleadojerarquia). En ese caso,
   * <see cref="invalidateDirectoresCache"/> resetea la caché para que la
   * próxima carga de Mis Autorizaciones / Equipo refleje al nuevo subordinado.
   */
  private getDirectoresCache$: Observable<DirectorAsignacion[]> | null = null;

  /**
   * Invalida la caché de getDirectores(). Llamar después de operaciones que
   * cambian la jerarquía (movempleadojerarquia.fkidempleadorhsuperior), como
   * el cambio de área de un empleado. Sin esto, el director nuevo no ve al
   * subordinado en "Mis Autorizaciones" hasta refrescar la sesión.
   */
  invalidateDirectoresCache(): void {
    this.getDirectoresCache$ = null;
  }

  /**
   * Los listados degradan a lista vacía en vez de romper la pantalla: un
   * tablero o un panel de autorizaciones sin datos se lee como "no hay nada",
   * que es tolerable; uno que revienta, no.
   */
  private listaOVacia<T>(pedir: () => Promise<T[] | null | undefined>): Observable<T[]> {
    return defer(() => from(pedir())).pipe(
      map((data) => data ?? []),
      catchError(() => of([] as T[])),
    );
  }

  /**
   * Estrecha `estatus` de `string` (lo que declara el contrato) a los seis
   * valores del dominio.
   *
   * La aserción vive AQUÍ, en el único punto por donde el contrato entra al
   * dominio, y no repartida por las páginas. Si el backend llegara a mandar un
   * séptimo estatus, este es el sitio donde ponerle un guard; hoy los seis son
   * los que produce su máquina de estados.
   */
  private comoSolicitudes(
    pedir: () => Promise<SolicitudVacacionesResponse[] | null | undefined>,
  ): Observable<SolicitudVacaciones[]> {
    return this.listaOVacia(pedir) as Observable<SolicitudVacaciones[]>;
  }

  private comoSolicitud(
    pedir: () => Promise<SolicitudVacacionesResponse | null | undefined>,
  ): Observable<SolicitudVacaciones> {
    return defer(() => from(pedir())).pipe(map((s) => s as SolicitudVacaciones));
  }

  // ── GET ALL ────────────────────────────────────────────────────────
  getAll(): Observable<SolicitudVacaciones[]> {
    return this.comoSolicitudes(() => this.api.getVacaciones());
  }

  // ── GET BY ID ──────────────────────────────────────────────────────
  getById(id: number): Observable<SolicitudVacaciones> {
    return this.comoSolicitud(() => this.api.getVacacionesById(id));
  }

  // ── GET BY EMPLEADO ────────────────────────────────────────────────
  getByEmpleado(empleadoId: number): Observable<SolicitudVacaciones[]> {
    return this.comoSolicitudes(() => this.api.empleado(empleadoId));
  }

  // ── GET PENDIENTES DIRECTOR (estatus = Pendiente) ──────────────────
  getPendientesDirector(): Observable<SolicitudVacaciones[]> {
    return this.comoSolicitudes(() => this.api.pendientes_director());
  }

  // ── GET DIRECTORES ─────────────────────────────────────────────────
  /**
   * Carga la lista de directores desde la tabla director_asignaciones.
   * Devuelve [] sin toast si la tabla no existe todavía.
   */
  getDirectores(): Observable<DirectorAsignacion[]> {
    if (!this.getDirectoresCache$) {
      this.getDirectoresCache$ = this.listaOVacia(() => this.api.directores()).pipe(
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }
    return this.getDirectoresCache$;
  }

  // ── GET PENDIENTES POR ÁREAS (director scope) ──────────────────────
  /**
   * `areas` viaja como ARREGLO: el endpoint declara un query repetible
   * (`?areas=A&areas=B`) y `HttpParams` repite la clave por elemento. Antes se
   * armaba la cadena a mano con `encodeURIComponent`; ahora lo hace el framework.
   */
  getPendientesPorAreas(areas: string[]): Observable<SolicitudVacaciones[]> {
    return this.comoSolicitudes(() => this.api.pendientes_misAreas(areas));
  }

  // ── GET PENDIENTES PARA DIRECTOR GENERAL ──────────────────────────
  // Devuelve solicitudes Pendiente cuyo empleadoId sea uno de los directores supervisados
  getPendientesParaDG(directorEmpleadoIds: number[]): Observable<SolicitudVacaciones[]> {
    return this.comoSolicitudes(() => this.api.pendientes_dg(directorEmpleadoIds));
  }

  // ── GET PENDIENTES RH (estatus = Aprobada Director) ────────────────
  getPendientesRH(): Observable<SolicitudVacaciones[]> {
    return this.comoSolicitudes(() => this.api.pendientes_rh());
  }

  // ── CREATE ─────────────────────────────────────────────────────────
  create(request: CreateSolicitudRequest): Observable<SolicitudVacaciones> {
    return this.comoSolicitud(() => this.api.createVacaciones(request));
  }

  // ── APROBAR / RECHAZAR (flujo Director → RH) ───────────────────────
  aprobarDirector(id: number, request: AprobarRechazarRequest): Observable<SolicitudVacaciones> {
    return this.comoSolicitud(() => this.api.aprobarDirector(id, request));
  }

  rechazarDirector(id: number, request: AprobarRechazarRequest): Observable<SolicitudVacaciones> {
    return this.comoSolicitud(() => this.api.rechazarDirector(id, request));
  }

  aprobarRH(id: number, request: AprobarRechazarRequest): Observable<SolicitudVacaciones> {
    return this.comoSolicitud(() => this.api.aprobarRh(id, request));
  }

  rechazarRH(id: number, request: AprobarRechazarRequest): Observable<SolicitudVacaciones> {
    return this.comoSolicitud(() => this.api.rechazarRh(id, request));
  }

  // ── EDITAR (admin) ─────────────────────────────────────────────────
  editarAdmin(id: number, request: EditarSolicitudAdminRequest): Observable<SolicitudVacaciones> {
    return this.comoSolicitud(() => this.api.admin(id, request));
  }

  // ── UPDATE ─────────────────────────────────────────────────────────
  update(id: number, request: UpdateSolicitudRequest): Observable<SolicitudVacaciones> {
    return this.comoSolicitud(() => this.api.updateVacacionesById(id, request));
  }

  // ── CANCELAR ───────────────────────────────────────────────────────
  cancelar(id: number): Observable<SolicitudVacaciones> {
    return this.comoSolicitud(() => this.api.cancelar(id));
  }

  // ── GET EQUIPO SALDO ──────────────────────────────────────────────
  /**
   * Devuelve el estado de vacaciones de todos los empleados del equipo del
   * director. Acepta áreas (director de área) o IDs de empleados (DG). El orden
   * por prioridad —críticos → advertencia → con pendientes → resto— lo resuelve
   * el backend.
   *
   * Los dos parámetros son queries REPETIBLES y viajan como arreglos.
   *
   * ⚠️ `excludeEmpleadoId` está en la firma pero NO se envía, y esto no es nuevo:
   * solo se aplicaba dentro de la rama de mock, así que en el camino real —el
   * único que corrió en cualquier ambiente— siempre se ignoró. El endpoint
   * tampoco lo declara. `mis-autorizaciones` lo pasa esperando que el director
   * no se vea a sí mismo en su equipo, y eso hoy no ocurre. Se conserva el
   * parámetro para no cambiar el comportamiento en un refactor; arreglarlo es
   * una decisión aparte —filtrar aquí o agregar el query al backend—.
   */
  getEquipoSaldo(options: {
    areas?: string[];
    empleadoIds?: number[];
    excludeEmpleadoId?: number;
  }): Observable<EmpleadoEquipoSaldo[]> {
    return this.listaOVacia(() =>
      this.api.equipoSaldo(options.areas ?? [], options.empleadoIds ?? []),
    ) as Observable<EmpleadoEquipoSaldo[]>;
  }

  // ── GET SALDO ──────────────────────────────────────────────────────
  /**
   * ÚNICA llamada que sigue por `HttpClient`, y no por comodidad: necesita el
   * CÓDIGO DE ESTADO. Un 503 significa que el servicio de saldos todavía no
   * está listo, y entonces se traga el error en silencio (`EMPTY`) en vez de
   * mostrarle un toast al usuario; cualquier otro estado sí se propaga.
   *
   * `ApiBaseService` no puede expresar eso: convierte el `HttpErrorResponse` en
   * un `Error` plano con el mensaje ya extraído, así que el `status` se pierde.
   * Pasar este método al generado silenciaría todos los errores o ninguno.
   *
   * `excluirSolicitudId` pide el saldo ANTES de esa solicitud. El normal ya
   * descuenta las pendientes, así que al editar una el ciclo viejo que ella
   * misma agotó llegaba vacío y el desglose atribuía sus días al ciclo nuevo.
   */
  getSaldo(empleadoId: number, excluirSolicitudId?: number): Observable<SaldoVacaciones> {
    const params =
      excluirSolicitudId != null ? { excluirSolicitudId: String(excluirSolicitudId) } : undefined;
    return this.http
      .get<ApiResponse<SaldoVacaciones>>(`${this.baseUrl}/saldo/${empleadoId}`, { params })
      .pipe(
        map((res) => res.object!),
        catchError((err: HttpErrorResponse) =>
          err.status === 503 ? EMPTY : throwError(() => err),
        ),
      );
  }

  // ── GET DASHBOARD ──────────────────────────────────────────────────
  getDashboard(): Observable<VacacionesDashboard> {
    return defer(() => from(this.api.dashboard())).pipe(map((d) => d!));
  }

  // ── GET CALENDARIO ─────────────────────────────────────────────────
  /**
   * Obtiene eventos de calendario para el rango indicado.
   *
   * Sin fechas, el rango va del primer día del mes en curso al último del mes
   * +3, que es el buffer con el que el calendario abre.
   */
  getCalendario(desde?: Date, hasta?: Date): Observable<VacacionesCalendario[]> {
    const hoy = new Date();
    const desdeDate = desde ?? new Date(hoy.getFullYear(), hoy.getMonth(), 1);
    const hastaDate = hasta ?? new Date(hoy.getFullYear(), hoy.getMonth() + 3, 0);

    return this.listaOVacia(() =>
      this.api.calendario(desdeDate.toISOString(), hastaDate.toISOString()),
    ) as Observable<VacacionesCalendario[]>;
  }
}
